import { Button, Div, Icon, Label } from "@grassrootly/rn-core";
import { GameActionsProps } from "../GamePreview.types";
import { useUser } from "../../../api/auth/AuthSelector";
import { hasChoosenAttendance, isAttending } from "../lib";
import { GameStatues } from "@grassrootly/api";
import { useAcceptAttendece, useDeclineAttendance } from "./useGameActions";
import { Alert } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useConfirmDialog } from "../../../layout/components/confirm-dialog";
import { useGameVotes } from "../hooks/useGameVotes";

export default function GameActions({ game }: GameActionsProps) {
  const user = useUser();
  const { accept, accepting } = useAcceptAttendece();
  const { decline, declining } = useDeclineAttendance();
  const navigation = useNavigation();
  const { prompt } = useConfirmDialog();
  const { votesCount, playerVoted } = useGameVotes({ gameId: game.id });

  const goToVote = () => navigation.navigate("vote", { id: game.id });
  const declineGame = () => decline(game.id);
  const acceptGame = () => accept(game.id);

  const reDecline = () =>
    prompt({
      title: "Change attendence",
      message: "You have accepted the attendece to this game. Do you want to change it and decline?",
      onYes: declineGame,
    });

  const reAccept = () =>
    prompt({
      title: "Change attendence",
      message: "You have declined the attendece to this game. Do you want to change it and accept?",
      onYes: acceptGame,
    });

  const isUserAttending = isAttending(game, user!);
  const votedAlready = user?.id ? playerVoted(user.id) : false;
  const totalVotes = votesCount ?? 0;

  // show vote button only if finished AND attending
  const canVote = game.status === GameStatues.FINISHED && isUserAttending;
  const showFinishedPill = game.status === GameStatues.FINISHED && !isUserAttending;

  return (
    <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter"]}>
      <Div>
        {/* SCHEDULED GAME */}
        {game.status === GameStatues.SCHEDULED ? (
          isUserAttending ? (
            <Button
              classNames={["mt-{-20}", "btn", "btnOutlinePrimary", "bgNeutralDarkerDark"]}
              isLoading={declining}
              disabled={declining}
              onPress={reDecline}
              right={{ name: "change_circle", size: 24, classNames: ["colorPrimaryMain"] }}
            >
              <Label type="l1Regular">Attending</Label>
            </Button>
          ) : hasChoosenAttendance(game, user!) ? (
            <Button
              classNames={["mt-{-20}", "btn", "btnOutlineDanger", "bgNeutralDarkerDark"]}
              onPress={reAccept}
              isLoading={accepting}
              disabled={accepting}
              right={{ name: "change_circle", size: 24, classNames: ["colorDangerMain"] }}
            >
              <Label type="l1Regular" classNames={["colorDangerMain"]}>
                Not Attending
              </Label>
            </Button>
          ) : (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                title="Accept"
                left={{ name: "check" }}
                classNames={["btn", "btnPrimary", "mt-{-20}", "ml-{10}"]}
                onPress={acceptGame}
                isLoading={accepting}
                disabled={accepting || declining}
              />
              <Button
                title="Decline"
                left={{ name: "close" }}
                onPress={declineGame}
                isLoading={declining}
                disabled={declining || accepting}
                classNames={["btn", "btnDanger", "mt-{-20}", "mr-{10}"]}
              />
            </div>
          )
        ) : null}
        {/* CONFIRMED GAME */}
        {game.status === GameStatues.CONFIRMED ? (
          <Div classNames={["flexColumn", "alignItemsCenter", "mt-{-20}", "justifyContentCenter"]}>
            <Div classNames={["btn", "br-{8}", "pv-{8}", "ph-{12}", "borderPrimary", "bw-{1}", "bgNeutralDarkerDark"]}>
              <Label>KICK-OFF {game.status}</Label>
            </Div>
          </Div>
        ) : null}
        {/* FINISHED GAME */}
        {game.status === GameStatues.FINISHED ? (
          <Div classNames={["flexColumn", "alignItemsCenter"]}>
            {isUserAttending ? (
              <>
                <Button
                  classNames={[
                    "btn",
                    "mt-{-20}",
                    ...(votedAlready ? ["btnOutlinePrimary", "bgNeutralDarkerDark"] : ["btnPrimary"]),
                  ]}
                  title={votedAlready ? "Voted" : "Vote"}
                  disabled={votedAlready}
                  onPress={goToVote}
                  left={{ name: "emoji_events" }}
                />
              </>
            ) : (
              <Div
                classNames={[
                  "mt-{-20}",
                  "btn",
                  "br-{8}",
                  "pv-{8}",
                  "ph-{12}",
                  "borderPrimary",
                  "bw-{1}",
                  "bgNeutralDarkerDark",
                ]}
              >
                <Label>{GameStatues.FINISHED}</Label>
              </Div>
            )}

            {/* vote count BELOW the button/pill */}
            <Label type="l4Regular" classNames={["mt-{5}", "colorPrimaryMain"]}>
              {totalVotes} player(s) voted
            </Label>
          </Div>
        ) : null}
        {/* FINALIZED GAME */}
        {game.status === GameStatues.FINALIZED ? (
          <Div classNames={["flexColumn", "alignItemsCenter", "mt-{-20}"]}>
            <Div classNames={["btn", "br-{8}", "pv-{8}", "ph-{12}", "borderPrimary", "bw-{1}", "bgNeutralDarkerDark"]}>
              <Label>{GameStatues.FINISHED}</Label>
            </Div>

            {/* vote count BELOW the pill */}
            <Label type="l4Regular" classNames={["mt-{5}", "colorPrimaryMain"]}>
              {totalVotes} player(s) voted
            </Label>
          </Div>
        ) : null}
        {/* CANCELED GAME */}
        {game.status === GameStatues.CANCELED ? (
          <Div
            classNames={["mt-{-20}", "btn", "btnOutlineDanger", "br-{8}", "pv-{8}", "ph-{12}", "bgNeutralDarkerDark"]}
          >
            <Label classNames={["colorDangerMain"]}>{game.status}</Label>
          </Div>
        ) : null}
      </Div>
    </Div>
  );
}
